import { Skeleton } from '@ds';
import { CaseNote as Note, CaseNoteMenuAction } from '@lp/features/CaseNoteCard/Note';
import { useContext, useMemo } from 'react';
import { AuthContext } from '@lp/core/auth';
import { useCaseNotesStore } from '@/store/case-notes.store';
import { CaseNote, CaseStatus } from '@/api/apis/case';
import { usePinCaseNoteMutationMutation } from '@/queries/case';
import { formatDate, getFullName, isAllowedStatus } from '@/utils/helpers';
import { useComposedEmployeeByIdQuery } from '@/queries/employee';

type NoteProps = {
  caseId: string;
  data: CaseNote;
  readonly?: boolean;
  light?: boolean;
  isScrollable?: boolean;
  status?: CaseStatus;
};

export const CaseNoteCard = ({ caseId, data, readonly, light, isScrollable = false, status }: NoteProps) => {
  const { profile } = useContext(AuthContext);
  const { mutate: changeCaseNotePin } = usePinCaseNoteMutationMutation();
  const setEditNote = useCaseNotesStore.use.setEditNote();
  const setRemoveNote = useCaseNotesStore.use.setRemoveNote();
  const fullName = getFullName(data?.user?.firstName, data?.user?.lastName);
  const isMe = useMemo(() => profile?.userId === data?.user?.id, [profile, data]);
  const isEdited = data.createdAt !== data.modifiedAt;
  const { data: caseNoteUserData } = useComposedEmployeeByIdQuery(data?.user?.employeeId || '');
  const isArchivedCase =
    !!status &&
    isAllowedStatus({
      currentStatus: status,
      statusFrom: CaseStatus.Finished,
      statusTo: CaseStatus.Closed,
    });
  const isReadOnlyByCase = readonly || isArchivedCase;

  const onMenuAction = (action: CaseNoteMenuAction) => {
    if (isReadOnlyByCase) return;
    switch (action) {
      case CaseNoteMenuAction.Pin:
        changeCaseNotePin({ caseId, noteId: data.noteId, isPinned: !data.isPinned });
        break;
      case CaseNoteMenuAction.Edit:
        setEditNote(data);
        break;
      case CaseNoteMenuAction.Delete:
        setRemoveNote(data);
        break;
      default:
        break;
    }
  };

  return (
    <Note
      title={fullName || 'Unknown user'}
      variant="lg"
      date={formatDate(data.modifiedAt, 'MMM d, HH:mm zzz')}
      isEdited={isEdited}
      isPinned={data.isPinned}
      isPinnedLight={light && data.isPinned}
      isReadOnly={isReadOnlyByCase}
      avatarProps={{ src: data?.user?.photoPath || '', name: fullName }}
      onMenuAction={onMenuAction}
      withEditMenuItem={!isArchivedCase && isMe}
      withDeleteMenuItem={!isReadOnlyByCase && isMe}
      caseNoteUserData={caseNoteUserData}
      isScrollable={isScrollable}
    >
      <div className="whitespace-pre-wrap wrap-break-word">{data.message}</div>
    </Note>
  );
};

CaseNoteCard.Skeleton = () => {
  return (
    <div className="flex flex-col gap-3 flex-1 bg-base-80 rounded-xl p-3">
      <div className="flex items-center gap-2 grow">
        <Skeleton className="flex rounded-full w-6 h-6" />
        <Skeleton className="h-3 w-2/5 rounded-lg" />
      </div>
      <div className="flex flex-col gap-3">
        <Skeleton className="h-2.5 w-3/5 rounded-lg" />
        <Skeleton className="h-2.5 w-4/5 rounded-lg" />
        <Skeleton className="h-2.5 w-2/5 rounded-lg" />
      </div>
    </div>
  );
};
